import {
  BROKER_SYNC_PROVIDER_LABELS, BROKER_SYNC_REVIEW_DATASET_LABELS, BROKER_SYNC_REVIEW_QUALITY_LABELS,
  BROKER_SYNC_REVIEW_QUALITY_OBJ, type BrokerSyncIssue, type BrokerSyncReviewScope,
  type BrokerSyncReviewCoverage, type BrokerSyncQualityReviewsResponse,
} from "@signals/shared";
import type { BrokerSyncIssuesResponse, BrokerSyncAccountStatus } from "./broker-sync-panel.types";
import { t, localeKey, formatDateTime } from "./broker-sync-panel-format.util";
import { redactKnownSecrets } from "./broker-sync-panel-error.util";
import { BrokerSyncIssueDetailsCard, brokerReviewDate, brokerReviewMoney } from "./broker-sync-issue-details";
import { Button } from "../_common";

function ReviewScopeSummary({ scope, coverage, secrets = [] }: { scope?: BrokerSyncReviewScope | undefined; coverage?: BrokerSyncReviewCoverage | undefined; secrets?: readonly string[] | undefined }) {
  return <div className="space-y-1 text-xs text-muted-foreground">
    {scope && <p>{BROKER_SYNC_REVIEW_DATASET_LABELS[scope.dataset][localeKey()]} · {scope.from ? `С ${brokerReviewDate(scope.from)}` : "Начало периода неизвестно"}{scope.to ? ` по ${brokerReviewDate(scope.to)}` : ""}; срез на {redactKnownSecrets(formatDateTime(scope.asOf), secrets)}.</p>}
    {coverage && !coverage.complete && <p className="text-amber-700" role="status">Полный состав не подтверждён. Известно записей: {coverage.known}{coverage.reported === null ? "; общее число неизвестно." : `; в результате проверки указано: ${coverage.reported}.`}</p>}
  </div>;
}

export function BrokerSyncIssues({
  issues,
  accounts,
  errorText,
  isLoading,
  managementCode,
  secretValues,
  onRetry,
  selectedScope,
  onPrepare,
}: {
  issues: BrokerSyncIssuesResponse | undefined;
  accounts: readonly BrokerSyncAccountStatus[];
  errorText: string | null;
  isLoading: boolean;
  managementCode: string;
  secretValues: readonly string[];
  onRetry?: (() => void) | undefined;
  selectedScope?: BrokerSyncReviewScope | undefined;
  onPrepare?: ((issue: BrokerSyncIssue) => void) | undefined;
}) {
  if (isLoading && !issues) return <p className="text-sm text-muted-foreground">{t("portfolio.brokerSync.issuesLoading")}</p>;
  const items = issues?.items ?? [];
  const byAccount = new Map(accounts.map((account) => [account.accountId, account]));
  const safe = (text: string) => redactKnownSecrets(text, [managementCode, ...secretValues]);
  return (
    <div className="min-w-0 space-y-3 [overflow-wrap:anywhere]">
      {errorText && <div role="alert" className="space-y-2 text-sm text-destructive">
        <p>{t("portfolio.brokerSync.issuesError")} {safe(errorText)}{issues ? " Последний полученный список сохранён; сейчас он не подтверждён." : ""}</p>
        {onRetry && <Button type="button" size="sm" variant="outline" onClick={onRetry}>Повторить загрузку проблем</Button>}
      </div>}
      <ReviewScopeSummary scope={issues?.scope ?? selectedScope} coverage={issues?.coverage} secrets={[managementCode, ...secretValues]} />
      {issues?.truncated ? (
        <p className="text-sm text-amber-700">
          {t("portfolio.brokerSync.issuesTruncated", { shown: items.length, total: issues.total })}
        </p>
      ) : null}
      {items.length === 0 && issues && !errorText ? <p className="text-sm text-muted-foreground">{issues.coverage?.complete === false
        ? "Подтверждённые записи этого среза пока недоступны; это не означает, что записей нет."
        : selectedScope || issues.scope ? "В выбранном списке записей нет." : t("portfolio.brokerSync.noIssues")}</p> : null}
      {items.map((issue, index) => <BrokerSyncIssueDetailsCard key={issue.id ?? `${issue.accountId}:${issue.operationKey}:${index}`}
        issue={issue} account={byAccount.get(issue.accountId)} managementCode={managementCode} secretValues={secretValues} onPrepare={onPrepare} />)}
    </div>
  );
}

export interface BrokerSyncQualityReviewsProps {
  reviews: BrokerSyncQualityReviewsResponse | undefined;
  accounts?: readonly BrokerSyncAccountStatus[] | undefined;
  errorText: string | null;
  isLoading: boolean;
  managementCode?: string | undefined;
  secretValues?: readonly string[] | undefined;
  onRetry?: (() => void) | undefined;
}

/** Quality is a view of saved income events. Every saved identity keeps its own card. */
export function BrokerSyncQualityReviews({ reviews, accounts = [], errorText, isLoading, managementCode = "", secretValues = [], onRetry }: BrokerSyncQualityReviewsProps) {
  const safe = (text: string) => redactKnownSecrets(text, [managementCode, ...secretValues]);
  const byAccount = new Map(accounts.map((account) => [account.accountId, account.accountName]));
  if (isLoading && !reviews) return <p className="text-sm text-muted-foreground">Загружаем сведения о выплатах…</p>;
  return <div className="min-w-0 space-y-3 [overflow-wrap:anywhere]">
    {errorText && <div role="alert" className="space-y-2 text-sm text-destructive">
      <p>Не удалось обновить сведения о выплатах. {safe(errorText)}{reviews ? " Последний полученный список сохранён; сейчас он не подтверждён." : ""}</p>
      {onRetry && <Button type="button" variant="outline" size="sm" onClick={onRetry}>Повторить загрузку выплат</Button>}
    </div>}
    <ReviewScopeSummary scope={reviews?.scope} coverage={reviews?.coverage} secrets={[managementCode, ...secretValues]} />
    {reviews?.truncated && <p className="text-sm text-amber-700">{t("portfolio.brokerSync.issuesTruncated", { shown: reviews.items.length, total: reviews.total })}</p>}
    {reviews && reviews.items.length === 0 && !errorText && <p className="text-sm text-muted-foreground">{reviews.coverage.complete ? "В выбранной группе выплат нет." : "Состав этой группы пока не подтверждён."}</p>}
    {reviews?.items.map((review) => {
      const confirmed = review.quality === BROKER_SYNC_REVIEW_QUALITY_OBJ.CONFIRMED_NET_UNKNOWN_BASIS;
      const instrument = [review.instrument.name, review.instrument.ticker, review.instrument.isin].filter(Boolean).join(" · ");
      return <article key={review.id} className="min-w-0 space-y-3 rounded-md border p-3 [overflow-wrap:anywhere]">
        <h4 className="font-medium">{BROKER_SYNC_REVIEW_QUALITY_LABELS[review.quality][localeKey()]}</h4>
        <p className="text-sm text-muted-foreground">{BROKER_SYNC_PROVIDER_LABELS[review.provider][localeKey()]} · {safe(byAccount.get(review.accountId) ?? review.accountName)}</p>
        <dl className="grid min-w-0 gap-2 text-sm sm:grid-cols-2">
          <div><dt className="text-muted-foreground">Дата выплаты</dt><dd>{brokerReviewDate(review.date)}</dd></div>
          <div><dt className="text-muted-foreground">Инструмент</dt><dd>{instrument ? safe(instrument) : "Инструмент не подтверждён"}</dd></div>
          <div><dt className="text-muted-foreground">{confirmed ? "Фактическое зачисление" : "Зачисление по сохранённой записи"}</dt><dd>{safe(brokerReviewMoney(review.creditedCash))}</dd></div>
          <div><dt className="text-muted-foreground">Сумма до налога</dt><dd>{review.grossUnknown ? "Неизвестно" : safe(brokerReviewMoney(review.gross))}</dd></div>
          <div><dt className="text-muted-foreground">Удержанный налог</dt><dd>{review.withholdingUnknown ? "Неизвестно" : safe(brokerReviewMoney(review.withholding))}</dd></div>
        </dl>
        <p className="text-sm"><span className="font-medium">Что уже учтено: </span>{safe(review.alreadyAccounted.text)}</p>
        <p className="text-sm">{confirmed ? "Зачисление подтверждено и сохранено. Неизвестные сумма до налога или налог не заменяются нулём и не считаются пропущенной операцией."
          : "Выплата сохранена, но её основание требует подтверждения. Сопоставьте её с выпиской; это отдельная группа качества, а не новая ошибка загрузки."}</p>
        <details className="min-w-0">
          <summary className="min-h-9 cursor-pointer py-2 text-sm text-muted-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">Технические подробности выплаты</summary>
          <p className="break-all text-xs text-muted-foreground">ID записи: {safe(review.id)}</p>
          <p className="break-all text-xs text-muted-foreground">ID выплаты: {safe(review.incomeEventId)}</p>
        </details>
      </article>;
    })}
  </div>;
}
